import type { Address } from "viem";
import { assertMerkleV4Config, type MerkleV4Config } from "./merkle-v4";

export type NativeV3TradeConfig = Readonly<{
  chainId: number;
  deploymentBlock: bigint;
  environment: string;
  token: Address;
  factory: Address;
  router: Address;
  pool: Address;
  vault: Address;
}>;
export type TradeConfig = NativeV3TradeConfig | MerkleV4Config;
export const isMerkleV4 = (config: TradeConfig): config is MerkleV4Config => "poolId" in config;

/** Historical V3 trade deployment for retained recovery/tests; NFT pins are independent. */
export const ELYSIUM_TRADE = Object.freeze({
  chainId: 99901,
  // TODO(Merkle): replace this previous-deployment block with the verified Merkle deployment boundary.
  deploymentBlock: 8617n,
  environment: "production",
  token: "0x419d62c375C30c7F13Aa340e79c68489D92E6630" as Address,
  factory: "0x5d2691d8ea4B54eD0b011ebCBb40210d93e9240B" as Address,
  router: "0xFb131D9be1dBB4b932AA9C1AcF683EF69947Ed11" as Address,
  // TODO(Merkle): replace this OLD pool address after the Merkle pool is created and linked.
  // Sync the real pool with BE; BE currently returns poolAddress=null (WAITING_FOR_LAUNCH).
  // Until then, bootstrap validation fails with ELYSIUM_DEPLOYMENT_MISMATCH.
  pool: "0x3ac3b0012255169756Cf84337e95B27c1185Ee2a" as Address,
  vault: "0x7876D9FE27acE99466F807816b86756D96DC2c60" as Address,
  poolDeployer: "0x493a886c210EEaDaE29BE3a32F89Cd27df0fD01d" as Address,
}) satisfies TradeConfig;

export const sameTradeAddress = (a: string | null | undefined, b: string) => a?.toLowerCase() === b.toLowerCase();

export function tradeConfigKey(config: TradeConfig = ELYSIUM_TRADE): string {
  if (isMerkleV4(config)) return JSON.stringify([config.chainId, config.deploymentBlock.toString(), config.environment,
    config.token, config.poolManager, config.poolId, config.router, config.routerHash, config.vault, config.hook,
    config.permit2, config.permit2Hash, config.stateView, config.poolKey].map(value => typeof value === "string" ? value.toLowerCase() : value));
  return JSON.stringify([config.chainId, config.deploymentBlock.toString(), config.environment,
    config.token.toLowerCase(), isMerkleV4(config) ? config.poolId : config.factory.toLowerCase(), config.router.toLowerCase(), config.pool.toLowerCase(), config.vault.toLowerCase()]);
}

/** Quotes do not depend on the fee vault or NFT deployment. */
export function assertTradeConfig(config: TradeConfig): void {
  if (isMerkleV4(config)) {
    assertMerkleV4Config(config);
    return;
  }
  if (config.chainId !== ELYSIUM_TRADE.chainId) throw new Error("ELYSIUM_CHAIN_MISMATCH");
  if (config.environment !== ELYSIUM_TRADE.environment || config.deploymentBlock !== ELYSIUM_TRADE.deploymentBlock ||
    !sameTradeAddress(config.token, ELYSIUM_TRADE.token) || !sameTradeAddress(config.factory, ELYSIUM_TRADE.factory) ||
    !sameTradeAddress(config.router, ELYSIUM_TRADE.router) || !sameTradeAddress(config.pool, ELYSIUM_TRADE.pool))
    throw new Error("ELYSIUM_TRADE_DEPLOYMENT_MISMATCH");
}
